import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useDispatch, useSelector } from 'src/redux/store';
import {
  getGroupDetails,
  updateGroup,
  leaveGroup,
  getGroupInviteLink,
  revokeGroupInviteLink,
  getGroupEvents
} from 'src/redux/slices/chat';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'src/@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from 'src/@/components/ui/avatar';
import { Switch } from 'src/@/components/ui/switch';
import { Separator } from 'src/@/components/ui/separator';

import AddParticipantsModal from './AddParticipantsModal';
import MemberMenu from './MemberMenu';

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString();
  } catch (e) { return ''; }
};

export default function GroupInfoDrawer({ open, onClose, chatId }) {
  const { t } = useTranslation('chat');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const chat = useSelector(state => state.chat?.chatsGrupos?.find(c => c._id === chatId));
  const events = useSelector(state => state.chat?.groupEvents || []);

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [openAdd, setOpenAdd] = useState(false);
  const [inviteLink, setInviteLink] = useState('');

  useEffect(() => {
    if (open && chatId) {
      dispatch(getGroupDetails(chatId));
      dispatch(getGroupEvents(chatId));
    }
  }, [open, chatId, dispatch]);

  useEffect(() => {
    if (chat) {
      setEditName(chat.groupName || '');
      setEditDescription(chat.groupDescription || '');
      setInviteLink(chat.groupInviteLink || '');
    }
  }, [chat]);

  const activeMembers = useMemo(
    () => (chat?.groupParticipants || []).filter(p => !p.leftAt),
    [chat]
  );

  const saveInfo = useCallback(async () => {
    const r = await dispatch(updateGroup(chatId, {
      subject: editName,
      description: editDescription
    }));
    enqueueSnackbar(
      r?.success ? t('groups.info.saved', 'Informações atualizadas') : (r?.message || t('whatsappBusiness.error', 'Erro')),
      { variant: r?.success ? 'success' : 'error' }
    );
    if (r?.success) setEditing(false);
  }, [dispatch, chatId, editName, editDescription, enqueueSnackbar, t]);

  const fetchInvite = useCallback(async () => {
    const r = await dispatch(getGroupInviteLink(chatId));
    if (r?.success) setInviteLink(r.data?.invite_link || '');
    else enqueueSnackbar(r?.message || t('whatsappBusiness.error', 'Erro'), { variant: 'error' });
  }, [dispatch, chatId, enqueueSnackbar]);

  const onRevoke = useCallback(async () => {
    const r = await dispatch(revokeGroupInviteLink(chatId));
    if (r?.success) {
      setInviteLink('');
      enqueueSnackbar(t('groups.info.linkRevoked', 'Link revogado'), { variant: 'success' });
    }
  }, [dispatch, chatId, enqueueSnackbar, t]);

  const onLeave = useCallback(async () => {
    if (!window.confirm(t('groups.info.leaveConfirm', 'Sair do grupo? Esta ação não pode ser desfeita.'))) return;
    const r = await dispatch(leaveGroup(chatId));
    if (r?.success) {
      enqueueSnackbar(t('groups.info.left', 'Você saiu do grupo'), { variant: 'success' });
      onClose?.();
    }
  }, [dispatch, chatId, enqueueSnackbar, t, onClose]);

  const onToggleSetting = useCallback(async (key, value) => {
    const settings = { [key]: value };
    await dispatch(updateGroup(chatId, { settings }));
  }, [dispatch, chatId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9998] animate-in fade-in duration-200" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-[90%] max-w-2xl bg-white dark:bg-neutral-900 shadow-xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 rounded-xl">
              <AvatarImage src={chat?.groupPhoto} />
              <AvatarFallback className="rounded-xl bg-violet-500 text-white">
                <Iconify icon="solar:users-group-rounded-bold" width={18} />
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-lg font-bold">{chat?.groupName}</h2>
              <p className="text-xs text-gray-500">{activeMembers.length} {t('groups.info.members', 'membros')}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><Iconify icon="eva:close-outline" width={20} /></Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="info" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="bg-transparent border-b border-gray-100 dark:border-neutral-800 rounded-none px-3">
            <TabsTrigger value="info">{t('groups.info.tabInfo', 'Info')}</TabsTrigger>
            <TabsTrigger value="members">{t('groups.info.tabMembers', 'Membros')}</TabsTrigger>
            <TabsTrigger value="invite">{t('groups.info.tabInvite', 'Link')}</TabsTrigger>
            <TabsTrigger value="events">{t('groups.info.tabEvents', 'Eventos')}</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="flex-1 overflow-y-auto px-6 py-5 space-y-5 mt-0">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('groups.info.name', 'Nome')}</label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} disabled={!editing} />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t('groups.info.description', 'Descrição')}</label>
              <Textarea value={editDescription} onChange={(e) => setEditDescription(e.target.value)} rows={3} disabled={!editing} />
            </div>
            <Separator className="bg-gray-200/60" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('groups.info.onlyAdminsCanSend', 'Só admins podem enviar mensagens')}</p>
                  <p className="text-[11px] text-gray-500">{t('groups.info.onlyAdminsCanSendHint', 'Restringe quem fala no grupo')}</p>
                </div>
                <Switch
                  checked={!!chat?.groupOnlyAdminsCanSend}
                  onCheckedChange={(v) => onToggleSetting('only_admins_can_send', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{t('groups.info.onlyAdminsCanEdit', 'Só admins podem editar info')}</p>
                  <p className="text-[11px] text-gray-500">{t('groups.info.onlyAdminsCanEditHint', 'Trava nome/descrição/foto')}</p>
                </div>
                <Switch
                  checked={!!chat?.groupOnlyAdminsCanEdit}
                  onCheckedChange={(v) => onToggleSetting('only_admins_can_edit', v)}
                />
              </div>
            </div>
            <Separator className="bg-gray-200/60" />
            <div className="flex items-center justify-end gap-2">
              {editing ? (
                <>
                  <Button variant="ghost" onClick={() => setEditing(false)}>{t('groups.create.cancel', 'Cancelar')}</Button>
                  <Button onClick={saveInfo} className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white">{t('groups.info.save', 'Salvar')}</Button>
                </>
              ) : (
                <Button onClick={() => setEditing(true)} variant="outline">
                  <Iconify icon="eva:edit-2-outline" width={14} className="mr-2" />
                  {t('groups.info.edit', 'Editar info')}
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="members" className="flex-1 overflow-y-auto px-6 py-5 mt-0">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {activeMembers.length} {t('groups.info.members', 'membros')}
              </span>
              <Button size="sm" onClick={() => setOpenAdd(true)} className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white">
                <Iconify icon="solar:add-circle-bold" width={14} className="mr-1" />
                {t('groups.participants.add', 'Adicionar')}
              </Button>
            </div>
            <div className="space-y-1">
              {activeMembers.map(m => (
                <div key={m.wa_id} className="flex items-center justify-between px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-neutral-800">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                      style={{ backgroundColor: m.color || '#6366f1' }}
                    >
                      {(m.name || m.wa_id || '').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{m.name || m.wa_id}</p>
                      <p className="text-[11px] text-gray-500 truncate">+{m.wa_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {m.isAdmin && (
                      <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                        {t('groups.participants.admin', 'admin')}
                      </span>
                    )}
                    <MemberMenu chatId={chatId} member={m} />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="invite" className="flex-1 overflow-y-auto px-6 py-5 mt-0">
            <p className="text-xs text-gray-500 mb-3">
              {t('groups.info.inviteHint', 'Compartilhe este link para que pessoas entrem no grupo.')}
            </p>
            {inviteLink ? (
              <div className="space-y-3">
                <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 px-3 py-2 text-sm font-mono break-all">
                  {inviteLink}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => navigator.clipboard.writeText(inviteLink)}>
                    <Iconify icon="solar:copy-bold" width={14} className="mr-1" />
                    {t('groups.info.copy', 'Copiar')}
                  </Button>
                  <Button variant="destructive" onClick={onRevoke}>
                    <Iconify icon="solar:trash-bin-trash-bold" width={14} className="mr-1" />
                    {t('groups.info.revokeLink', 'Revogar')}
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={fetchInvite} className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white">
                <Iconify icon="solar:link-bold" width={14} className="mr-1" />
                {t('groups.info.generateLink', 'Gerar link de convite')}
              </Button>
            )}
          </TabsContent>

          <TabsContent value="events" className="flex-1 overflow-y-auto px-6 py-5 mt-0">
            {(!events || events.length === 0) ? (
              <div className="text-xs text-gray-500 text-center py-8">{t('groups.info.noEvents', 'Sem eventos registrados')}</div>
            ) : (
              <ul className="space-y-2">
                {events.map(ev => (
                  <li key={ev._id} className="flex items-start gap-3 px-3 py-2 rounded-xl bg-gray-50 dark:bg-neutral-800">
                    <Iconify icon="solar:history-bold" width={14} className="mt-0.5 text-gray-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm">{t(`groups.events.${ev.type}`, ev.type)}</p>
                      <p className="text-[10px] text-gray-500">{formatDate(ev.timestamp)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-neutral-800 bg-gray-50/80 dark:bg-neutral-900/80">
          <Button variant="destructive" onClick={onLeave}>
            <Iconify icon="solar:logout-3-bold" width={14} className="mr-1" />
            {t('groups.info.leaveGroup', 'Sair do grupo')}
          </Button>
          <Button variant="ghost" onClick={onClose}>{t('groups.create.cancel', 'Fechar')}</Button>
        </div>
      </div>

      {openAdd && (
        <AddParticipantsModal open={openAdd} onClose={() => setOpenAdd(false)} chatId={chatId} />
      )}
    </div>
  );
}
